import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Users, Wand2, Copy, Check, LogIn, ArrowLeft, Loader2, Shield, Link2, Trash2, ExternalLink, UserPlus, X, AlertTriangle, Sparkles, Pencil, Lock, RefreshCw, Clock, Eye, ChevronDown, CreditCard, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import AdminBillingSection from './AdminBillingSection';
import AdminInboxSection from './AdminInboxSection';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const MAGIC_LINK_DOMAIN = 'https://app.xchannels.ai';

export default function Admin() {
  const { user, getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [magicLinks, setMagicLinks] = useState([]);
  const [generatingLink, setGeneratingLink] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingTokens, setEditingTokens] = useState(null); // user_id
  const [tokenInput, setTokenInput] = useState('');
  const [editingEmail, setEditingEmail] = useState(null); // user_id
  const [emailInput, setEmailInput] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('admin_unlocked') === '1');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [imageLogs, setImageLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  // ── Package management (new columns) ──────────────────────────────────
  const [pkgSaving, setPkgSaving] = useState(null); // user_id currently saving
  const [customModal, setCustomModal] = useState(null); // { userId, credits_monthly, channels, video_generation, search_placement }

  const PACKAGE_OPTIONS = [
    { value: 'trial',   label: 'Trial'   },
    { value: 'starter', label: 'Starter' },
    { value: 'growth',  label: 'Growth'  },
    { value: 'custom',  label: 'Custom'  },
  ];

  const formatDeDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso; }
  };

  const updatePackageApi = async (userId, payload) => {
    setPkgSaving(userId);
    try {
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : getAuthHeader();
      const res = await axios.post(
        `${API}/admin/users/${userId}/subscription`,
        payload,
        { headers }
      );
      // Merge the updated subscription back into the user row
      const updated = res.data || {};
      setUsers(prev => prev.map(u =>
        u.id === userId
          ? { ...u, subscription: {
              ...(u.subscription || {}),
              plan: updated.plan,
              billing_cycle: updated.billing_cycle,
              credits_total: updated.credits_total,
              credits_used: updated.credits_used,
              trial_start_date: updated.trial_start_date,
              trial_end_date: updated.trial_end_date,        // admin override (null if cleared)
              trial_expires_at: updated.trial_expires_at,    // effective end (override or +7d)
              credits_reset_date: updated.credits_reset_date,
              features: updated.features,
              status: updated.status,
            } }
          : u
      ));
      toast.success('Paket aktualisiert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update fehlgeschlagen');
    } finally {
      setPkgSaving(null);
    }
  };

  const handlePackageChange = (user, newPackage) => {
    if (newPackage === 'custom') {
      const s = user.subscription || {};
      const f = s.features || {};
      setCustomModal({
        userId: user.id,
        email: user.email,
        credits_monthly: s.credits_total || 0,
        channels: f.social_channels ?? 1,
        video_generation: !!f.video,
        search_placement: !!f.press_distribution,
      });
      return;
    }
    updatePackageApi(user.id, { package: newPackage });
  };

  const handleStartDateChange = (userId, newDate) => {
    if (!newDate) return;
    updatePackageApi(userId, {
      package: users.find(x => x.id === userId)?.subscription?.plan || 'trial',
      package_start_date: newDate,
    });
  };

  const handleResetDateChange = (userId, newDate) => {
    if (!newDate) return;
    updatePackageApi(userId, {
      package: users.find(x => x.id === userId)?.subscription?.plan || 'trial',
      package_reset_date: newDate,
    });
  };

  const handleEndDateChange = (userId, newDate) => {
    // newDate is a string: a date ("YYYY-MM-DD") to set, or "" to clear the override.
    // We pass the empty string through so the backend can distinguish
    // "clear override" (empty string) from "field not touched" (field omitted).
    updatePackageApi(userId, {
      package: users.find(x => x.id === userId)?.subscription?.plan || 'trial',
      package_end_date: newDate ?? '',
    });
  };

  const saveCustomModal = () => {
    if (!customModal) return;
    updatePackageApi(customModal.userId, {
      package: 'custom',
      custom_config: {
        credits_monthly: Number(customModal.credits_monthly) || 0,
        channels: Number(customModal.channels) || 0,
        video_generation: !!customModal.video_generation,
        search_placement: !!customModal.search_placement,
      },
    });
    setCustomModal(null);
  };
  // ──────────────────────────────────────────────────────────────────────

  const isAdmin = user?.email === 'ashley@xchannels.ai';
  const adminToken = localStorage.getItem('admin_original_token');
  const isCurrentlyImpersonating = !!adminToken;

  useEffect(() => {
    if (!user) return; // Wait for auth to load
    if (!isAdmin && !isCurrentlyImpersonating) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const effectiveHeaders = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : headers;

      const [usersRes, linksRes] = await Promise.all([
        axios.get(`${API}/admin/all-users`, { headers: effectiveHeaders }),
        axios.get(`${API}/admin/magic-links`, { headers: effectiveHeaders }),
      ]);
      setUsers(usersRes.data.users || []);
      setMagicLinks(linksRes.data.links || []);
    } catch (err) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadImageLogs = async () => {
    setLogsLoading(true);
    try {
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : getAuthHeader();
      const res = await axios.get(`${API}/admin/image-logs?limit=100`, { headers });
      setImageLogs(res.data.logs || []);
    } catch {
      toast.error('Logs konnten nicht geladen werden');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleImpersonate = async (targetUserId) => {
    try {
      setImpersonating(true);
      const currentToken = localStorage.getItem('token');
      // Save admin token for "back to admin" functionality
      if (!adminToken) {
        localStorage.setItem('admin_original_token', currentToken);
      }

      const headers = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : getAuthHeader();

      const res = await axios.post(`${API}/admin/impersonate/${targetUserId}`, {}, { headers });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Eingeloggt als ${res.data.user.email}`);
      window.location.href = '/app/kreation';
    } catch (err) {
      toast.error('Impersonate fehlgeschlagen');
    } finally {
      setImpersonating(false);
    }
  };

  const handleBackToAdmin = () => {
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_original_token');
      localStorage.removeItem('user');
      toast.success('Zurück zum Admin-Account');
      window.location.href = '/app/admin';
    }
  };

  const handleGenerateMagicLink = async (email) => {
    setGeneratingLink(email);
    try {
      const headers = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : getAuthHeader();

      await axios.post(`${API}/admin/magic-link`, {
        email,
        expires_days: 7,
        redirect_path: '/app/kreation',
      }, { headers });

      toast.success(`Magic Link für ${email} erstellt!`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Magic Links');
    } finally {
      setGeneratingLink(null);
    }
  };

  const handleCopyLink = useCallback(async (token) => {
    const fullUrl = `${MAGIC_LINK_DOMAIN}/magic-login?token=${token}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = fullUrl;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedToken(token);
    toast.success('Link kopiert!');
    setTimeout(() => setCopiedToken(null), 2000);
  }, []);

  const getUserMagicLink = useCallback((email) => {
    return magicLinks.find(l => l.email === email) || null;
  }, [magicLinks]);

  const handleDeleteLink = async (linkId) => {
    try {
      const headers = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : getAuthHeader();
      await axios.delete(`${API}/admin/magic-link/${linkId}`, { headers });
      setMagicLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Magic Link gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) {
      toast.error('Name und Email sind Pflichtfelder');
      return;
    }
    setCreatingUser(true);
    try {
      const headers = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : getAuthHeader();
      await axios.post(`${API}/admin/create-user`, {
        name: newUserName.trim(),
        email: newUserEmail.trim(),
      }, { headers });
      toast.success(`User ${newUserEmail} erstellt`);
      setShowCreateUser(false);
      setNewUserName('');
      setNewUserEmail('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const headers = adminToken
        ? { Authorization: `Bearer ${adminToken}` }
        : getAuthHeader();
      const res = await axios.delete(`${API}/admin/delete-user/${userId}`, { headers });
      toast.success(`User ${res.data.deleted_email} gelöscht`);
      setDeleteConfirm(null);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleSetTokens = async (userId) => {
    const limit = parseInt(tokenInput);
    if (isNaN(limit) || limit < 0) { toast.error('Ungültiger Wert'); return; }
    try {
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : getAuthHeader();
      await axios.put(`${API}/admin/set-tokens/${userId}`, { limit }, { headers });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, llmLimitTokens: limit } : u));
      setEditingTokens(null);
      toast.success('Token-Limit aktualisiert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const openEditEmail = (user) => {
    setEditingEmail(user.id);
    setEmailInput(user.email || '');
  };

  const handleSaveEmail = async (userId) => {
    const newEmail = (emailInput || '').trim().toLowerCase();
    if (!newEmail) { toast.error('E-Mail darf nicht leer sein'); return; }
    setEmailSaving(true);
    try {
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : getAuthHeader();
      const res = await axios.put(`${API}/admin/update-email/${userId}`, { email: newEmail }, { headers });
      const finalEmail = res.data?.email || newEmail;
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, email: finalEmail } : u));
      setEditingEmail(null);
      toast.success(res.data?.changed === false ? 'E-Mail unverändert' : 'E-Mail aktualisiert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleOnboarding = async (userId) => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!adminToken) localStorage.setItem('admin_original_token', currentToken);
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : getAuthHeader();
      const res = await axios.post(`${API}/admin/impersonate/${userId}`, {}, { headers });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/onboarding';
    } catch (err) {
      toast.error('Fehler beim Impersonate');
    }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

  if (!isAdmin && !isCurrentlyImpersonating) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-[#5A6C7D] mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold mb-2">Nur für Admins</h2>
          <p className="text-[#A0AEC0]">Du hast keinen Zugang zu diesem Bereich.</p>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    const handleUnlock = () => {
      if (pinInput === '2r739t9wbsouf26320hns') {
        sessionStorage.setItem('admin_unlocked', '1');
        setUnlocked(true);
        setPinError(false);
      } else {
        setPinError(true);
        setPinInput('');
      }
    };
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="bg-[#1A2B42] p-8 rounded-lg max-w-sm w-full space-y-4" data-testid="admin-pin-gate">
          <div className="text-center">
            <Lock className="w-12 h-12 text-[#00D9C8] mx-auto mb-3" />
            <h2 className="text-lg text-white font-semibold">Admin-Zugang</h2>
            <p className="text-[#A0AEC0] text-sm mt-1">Passwort eingeben</p>
          </div>
          <Input
            type="password"
            value={pinInput}
            onChange={(e) => { setPinInput(e.target.value); setPinError(false); }}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
            placeholder="Passwort"
            className={`bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-center ${pinError ? 'border-red-500' : ''}`}
            autoFocus
            data-testid="admin-pin-input"
          />
          {pinError && <p className="text-red-400 text-xs text-center">Falsches Passwort</p>}
          <Button onClick={handleUnlock} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-medium" data-testid="admin-pin-submit">
            Entsperren
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-panel">
      {/* Impersonation Banner */}
      {isCurrentlyImpersonating && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center justify-between" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">
              Eingeloggt als <span className="text-white">{user?.email}</span> (Admin-Impersonation)
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleBackToAdmin}
            className="bg-amber-500 hover:bg-amber-600 text-black h-7 text-xs"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Zurück zum Admin
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="admin-title">
            <Shield className="w-6 h-6 text-[#00D9C8]" />
            Admin Panel
          </h1>
          <p className="text-[#A0AEC0] text-sm mt-1">{users.length} User registriert</p>
        </div>
        <Button
          onClick={() => setShowCreateUser(true)}
          className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-medium"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Neuer User
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-[#0A1628] rounded-lg p-1" data-testid="admin-tabs">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="tab-users"
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" />
          Users ({users.length})
        </button>
        <button
          onClick={() => { setActiveTab('logs'); if (imageLogs.length === 0) loadImageLogs(); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="tab-image-logs"
        >
          <Wand2 className="w-3.5 h-3.5 inline mr-1.5" />
          Image Logs
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'billing' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="tab-billing"
        >
          <CreditCard className="w-3.5 h-3.5 inline mr-1.5" />
          Abrechnung
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'messages' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="tab-messages"
        >
          <Mail className="w-3.5 h-3.5 inline mr-1.5" />
          Nachrichten
        </button>
      </div>

      {activeTab === 'billing' && (
        <AdminBillingSection getAuthHeader={getAuthHeader} />
      )}
      {activeTab === 'messages' && (
        <AdminInboxSection getAuthHeader={getAuthHeader} />
      )}

      {activeTab === 'logs' && (
        <div className="space-y-3" data-testid="image-logs-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Bild-Generierung Logs</h2>
            <Button size="sm" variant="ghost" onClick={loadImageLogs} disabled={logsLoading} className="text-[#A0AEC0] hover:text-white text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${logsLoading ? 'animate-spin' : ''}`} /> Aktualisieren
            </Button>
          </div>
          {logsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" /></div>
          ) : imageLogs.length === 0 ? (
            <p className="text-[#5A6C7D] text-sm text-center py-8">Noch keine Logs vorhanden</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-[#1A2B42]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0A1628] text-[#5A6C7D] text-xs uppercase tracking-wider">
                    <th className="w-8 px-2 py-2"></th>
                    <th className="text-left px-3 py-2">Zeit</th>
                    <th className="text-left px-3 py-2">Modell</th>
                    <th className="text-center px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Plattform</th>
                    <th className="text-left px-3 py-2">Prompt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1A2B42]">
                  {imageLogs.map((log, i) => (
                    <React.Fragment key={i}>
                      <tr className="hover:bg-[#1A2B42]/30 cursor-pointer" onClick={() => setExpandedLog(expandedLog === i ? null : i)}>
                        <td className="px-2 py-2 text-[#5A6C7D]">
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedLog === i ? 'rotate-180' : ''}`} />
                        </td>
                        <td className="px-3 py-2 text-[#A0AEC0] text-xs whitespace-nowrap">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                            log.model?.includes('google') ? 'bg-blue-500/20 text-blue-400' :
                            log.model?.includes('imagen') ? 'bg-purple-500/20 text-purple-400' :
                            log.model?.includes('openai') ? 'bg-green-500/20 text-green-400' :
                            'bg-[#5A6C7D]/20 text-[#A0AEC0]'
                          }`}>
                            {log.model || '?'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {log.status === 'success' ? (
                            <span className="text-green-400 text-xs font-medium">OK</span>
                          ) : log.status === 'all_failed' ? (
                            <span className="text-red-400 text-xs font-medium">ALLE FEHLGESCHLAGEN</span>
                          ) : (
                            <span className="text-yellow-400 text-xs font-medium">FAIL</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[#A0AEC0] text-xs">{log.platform || '—'}</td>
                        <td className="px-3 py-2 text-[#A0AEC0] text-xs max-w-[300px] truncate">{log.prompt?.slice(0, 80) || '—'}{log.prompt?.length > 80 ? '...' : ''}</td>
                      </tr>
                      {expandedLog === i && (
                        <tr className="bg-[#0A1628]/50">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="space-y-3">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-[#5A6C7D] mb-1 font-medium">Vollständiger Prompt</p>
                                <pre className="text-xs text-[#A0AEC0] whitespace-pre-wrap bg-[#0A1628] rounded-lg p-3 max-h-[200px] overflow-y-auto border border-[#1A2B42] font-mono leading-relaxed">{log.prompt || '—'}</pre>
                              </div>
                              {log.error && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-red-400/80 mb-1 font-medium">Fehler</p>
                                  <pre className="text-xs text-red-400/90 whitespace-pre-wrap bg-red-500/5 rounded-lg p-3 max-h-[150px] overflow-y-auto border border-red-500/20 font-mono leading-relaxed">{log.error}</pre>
                                </div>
                              )}
                              <div className="flex gap-4 text-[10px] text-[#5A6C7D]">
                                <span>Ref-Image: {log.has_ref_image ? 'Ja' : 'Nein'}</span>
                                <span>Style-Refs: {log.style_refs || 0}</span>
                                {log.data_length > 0 && <span>Bildgröße: {Math.round(log.data_length / 1024)}KB</span>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && <>
      {/* Create User Form */}
      {showCreateUser && (
        <div className="bg-[#1A2B42] border border-[#00D9C8]/30 rounded-lg p-4 space-y-3" data-testid="create-user-form">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#00D9C8]" />
              Neuen User anlegen
            </h3>
            <button onClick={() => setShowCreateUser(false)} className="text-[#5A6C7D] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3">
            <Input
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              placeholder="Name / Firmenname"
              className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
              data-testid="create-user-name"
            />
            <Input
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="E-Mail-Adresse"
              type="email"
              className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateUser()}
              data-testid="create-user-email"
            />
            <Button
              onClick={handleCreateUser}
              disabled={creatingUser || !newUserName.trim() || !newUserEmail.trim()}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] px-6"
              data-testid="create-user-submit"
            >
              {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Anlegen'}
            </Button>
          </div>
          <p className="text-[#5A6C7D] text-xs">
            User wird ohne Passwort erstellt. Zugang per Magic Link oder Impersonate.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A6C7D]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="User suchen..."
          className="pl-10 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
          data-testid="admin-search"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#00D9C8] animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1A2B42]/50 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="bg-[#0A1628] text-[#5A6C7D] text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-3 py-3">Package</th>
                  <th className="text-left px-3 py-3">Start Date</th>
                  <th className="text-left px-3 py-3">Trial-Ende</th>
                  <th className="text-left px-3 py-3">Next Reset</th>
                  <th className="text-center px-3 py-3">Content</th>
                  <th className="text-center px-3 py-3">Brand</th>
                  <th className="text-center px-3 py-3">Letzter Login</th>
                  <th className="text-center px-3 py-3">Credits</th>
                  <th className="text-left px-3 py-3">Magic Link</th>
                  <th className="text-right px-4 py-3">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0A1628]/50">
                {filteredUsers.map(u => (
                  <tr key={u.id} className="hover:bg-[#1A2B42] transition-colors" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium text-sm">{u.name || '—'}</p>
                        {editingEmail === u.id ? (
                          <div className="flex items-center gap-1 mt-1" data-testid={`email-edit-row-${u.id}`}>
                            <input
                              type="email"
                              value={emailInput}
                              onChange={(e) => setEmailInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !emailSaving) {
                                  e.preventDefault();
                                  handleSaveEmail(u.id);
                                } else if (e.key === 'Escape') {
                                  setEditingEmail(null);
                                }
                              }}
                              autoFocus
                              className="bg-[#0A1628] border border-[#1A2B42] focus:border-[#00D9C8] text-white text-xs px-2 py-1 rounded w-52 outline-none"
                              data-testid={`email-input-${u.id}`}
                            />
                            <button
                              onClick={() => handleSaveEmail(u.id)}
                              disabled={emailSaving}
                              title="Speichern"
                              className="p-1 text-[#00D9C8] hover:bg-[#0A1628] rounded disabled:opacity-50"
                              data-testid={`email-save-${u.id}`}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setEditingEmail(null)}
                              title="Abbrechen"
                              className="p-1 text-[#5A6C7D] hover:text-white hover:bg-[#0A1628] rounded"
                              data-testid={`email-cancel-${u.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 group">
                            <p className="text-[#5A6C7D] text-xs">{u.email}</p>
                            {!u.is_admin && (
                              <button
                                onClick={() => openEditEmail(u)}
                                title="E-Mail ändern"
                                className="p-0.5 text-[#5A6C7D] hover:text-[#00D9C8] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`email-edit-btn-${u.id}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* ─── Package column (inline dropdown) ──────────────── */}
                    <td className="px-3 py-3" data-testid={`pkg-cell-${u.email}`}>
                      {(() => {
                        const rawPlan = u.subscription?.plan || null;
                        // Map legacy / admin-only plans into the 4 canonical options
                        const legacyMap = { free: 'trial', pro: 'growth', unlimited: 'custom' };
                        const normalised = rawPlan
                          ? (legacyMap[rawPlan] || (PACKAGE_OPTIONS.some(p => p.value === rawPlan) ? rawPlan : 'trial'))
                          : 'trial';
                        return (
                          <select
                            data-testid={`pkg-select-${u.email}`}
                            value={normalised}
                            onChange={(e) => handlePackageChange(u, e.target.value)}
                            disabled={pkgSaving === u.id}
                            className={`bg-[#0A1628] text-white text-xs border rounded px-2 py-1 outline-none min-w-[100px] appearance-none ${
                              pkgSaving === u.id ? 'opacity-50 cursor-wait' : 'border-[#1A2B42] hover:border-[#00D9C8]'
                            }`}
                          >
                            <option value="trial">Trial</option>
                            <option value="starter">Starter</option>
                            <option value="growth">Growth</option>
                            <option value="custom">Custom</option>
                          </select>
                        );
                      })()}
                      {u.subscription?.billing_cycle && (
                        <div className="text-[10px] text-[#5A6C7D] mt-1">
                          {u.subscription.billing_cycle === 'yearly' ? 'Jährlich' : 'Monatlich'}
                        </div>
                      )}
                    </td>
                    {/* ─── Start Date column (inline date picker) ──────── */}
                    <td className="px-3 py-3 text-xs" data-testid={`start-date-cell-${u.email}`}>
                      <label className="inline-flex flex-col cursor-pointer group relative">
                        <span className="text-[#A0AEC0] group-hover:text-white transition-colors">
                          {u.subscription?.trial_start_date
                            ? formatDeDate(u.subscription.trial_start_date)
                            : <span className="text-[#5A6C7D]">—</span>}
                        </span>
                        <input
                          type="date"
                          data-testid={`start-date-input-${u.email}`}
                          value={u.subscription?.trial_start_date || ''}
                          onChange={(e) => handleStartDateChange(u.id, e.target.value)}
                          disabled={pkgSaving === u.id}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </label>
                    </td>
                    {/* ─── Trial-Ende column (admin override; only relevant for trial plan) ── */}
                    <td className="px-3 py-3 text-xs" data-testid={`end-date-cell-${u.email}`}>
                      {(() => {
                        const isTrialUser = (u.subscription?.plan || 'trial') === 'trial';
                        const override = u.subscription?.trial_end_date || '';
                        const effective = u.subscription?.trial_expires_at || '';
                        const displayDate = override || effective;
                        return (
                          <label
                            className={`inline-flex flex-col cursor-pointer group relative ${isTrialUser ? '' : 'opacity-40'}`}
                            title={isTrialUser
                              ? (override ? 'Admin-Override aktiv — Klick zum Ändern' : 'Standard: Start + 7 Tage. Klick zum Override.')
                              : 'Nur für Trial-User relevant'}
                          >
                            <span className="text-[#A0AEC0] group-hover:text-white transition-colors">
                              {displayDate
                                ? formatDeDate(displayDate)
                                : <span className="text-[#5A6C7D]">—</span>}
                              {override && <span className="ml-1 text-[9px] text-amber-400">(override)</span>}
                            </span>
                            <input
                              type="date"
                              data-testid={`end-date-input-${u.email}`}
                              value={override || ''}
                              min={u.subscription?.trial_start_date || undefined}
                              onChange={(e) => handleEndDateChange(u.id, e.target.value)}
                              disabled={!isTrialUser || pkgSaving === u.id}
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                          </label>
                        );
                      })()}
                    </td>
                    {/* ─── Next Reset column (inline date picker) ───────── */}
                    <td className="px-3 py-3 text-xs" data-testid={`reset-date-cell-${u.email}`}>
                      <label className="inline-flex flex-col cursor-pointer group relative">
                        <span className="text-[#A0AEC0] group-hover:text-white transition-colors">
                          {u.subscription?.credits_reset_date
                            ? formatDeDate(u.subscription.credits_reset_date)
                            : <span className="text-[#5A6C7D]">—</span>}
                        </span>
                        <input
                          type="date"
                          data-testid={`reset-date-input-${u.email}`}
                          value={u.subscription?.credits_reset_date || ''}
                          min={u.subscription?.trial_start_date || undefined}
                          onChange={(e) => handleResetDateChange(u.id, e.target.value)}
                          disabled={pkgSaving === u.id}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </label>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-medium ${
                        u.content_count > 0 ? 'bg-[#00D9C8]/10 text-[#00D9C8]' : 'text-[#5A6C7D]'
                      }`}>
                        {u.content_count || 0}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`text-xs ${u.has_brand_dna ? 'text-green-400' : 'text-[#5A6C7D]'}`}>
                        {u.has_brand_dna ? 'Ja' : '—'}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3 text-[#A0AEC0] text-xs">
                      {formatDate(u.last_login)}
                    </td>
                    <td className="text-center px-3 py-3 text-xs">
                      {(() => {
                        const s = u.subscription || {};
                        const used = Number(s.credits_used || 0);
                        const total = Number(s.credits_total || 0);
                        const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(n);
                        const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                        const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-[#00D9C8]';
                        return (
                          <div
                            className="min-w-[90px]"
                            title={`Credits (neue Skala)${total ? ` · ${used} / ${total}` : ''}`}
                            data-testid={`credits-cell-${u.email}`}
                          >
                            <div className="text-[#A0AEC0]">
                              {fmt(used)}
                              <span className="text-[#5A6C7D]">/{total ? fmt(total) : '0'}</span>
                            </div>
                            {total > 0 && (
                              <div className="h-1 bg-[#1A2B42] rounded mt-1 overflow-hidden">
                                <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Magic Link Column */}
                    <td className="px-3 py-3" data-testid={`magic-link-cell-${u.email}`}>
                      {(() => {
                        const ml = getUserMagicLink(u.email);
                        if (!ml) {
                          return (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGenerateMagicLink(u.email)}
                              disabled={generatingLink === u.email}
                              className="h-7 px-2 text-[#5A6C7D] hover:text-[#00D9C8] hover:bg-[#0A1628] text-xs"
                              data-testid={`create-magic-link-${u.email}`}
                            >
                              {generatingLink === u.email ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Link2 className="w-3 h-3 mr-1" />
                              )}
                              Erstellen
                            </Button>
                          );
                        }
                        return (
                          <div className="space-y-1.5 min-w-[200px]">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleCopyLink(ml.token)}
                                className="flex items-center gap-1 bg-[#0A1628] rounded px-2 py-1 text-[10px] font-mono text-[#00D9C8] hover:bg-[#0A1628]/80 transition-colors max-w-[180px] truncate cursor-pointer"
                                title="Klicken zum Kopieren"
                                data-testid={`copy-link-${u.email}`}
                              >
                                {copiedToken === ml.token ? (
                                  <Check className="w-2.5 h-2.5 text-green-400 shrink-0" />
                                ) : (
                                  <Copy className="w-2.5 h-2.5 shrink-0" />
                                )}
                                <span className="truncate">{ml.token.slice(0, 20)}...</span>
                              </button>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="flex items-center gap-1 text-[#A0AEC0]">
                                <Clock className="w-2.5 h-2.5" />
                                {formatDate(ml.expires_at)}
                              </span>
                              <span className="flex items-center gap-1 text-[#A0AEC0]">
                                <Eye className="w-2.5 h-2.5" />
                                {ml.use_count || 0}x
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleGenerateMagicLink(u.email)}
                                disabled={generatingLink === u.email}
                                className="h-5 px-1.5 text-[10px] text-[#A0AEC0] hover:text-[#00D9C8] hover:bg-[#0A1628]"
                                title="Neuen Link generieren (alter wird ungültig)"
                                data-testid={`regenerate-link-${u.email}`}
                              >
                                {generatingLink === u.email ? (
                                  <Loader2 className="w-2.5 h-2.5 animate-spin mr-1" />
                                ) : (
                                  <RefreshCw className="w-2.5 h-2.5 mr-1" />
                                )}
                                Regenerieren
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(`/magic-login?token=${ml.token}`, '_blank')}
                                className="h-5 px-1.5 text-[10px] text-[#A0AEC0] hover:text-white hover:bg-[#0A1628]"
                                title="Link in neuem Tab testen"
                              >
                                <ExternalLink className="w-2.5 h-2.5 mr-1" />
                                Testen
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteLink(ml.id)}
                                className="h-5 px-1.5 text-[10px] text-[#5A6C7D] hover:text-red-400 hover:bg-[#0A1628]"
                                title="Link löschen"
                              >
                                <Trash2 className="w-2.5 h-2.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-right px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOnboarding(u.id)}
                          className="h-7 px-2 text-[#A0AEC0] hover:text-amber-400 hover:bg-[#0A1628]"
                          title="Onboarding starten"
                          data-testid={`onboarding-${u.email}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleImpersonate(u.id)}
                          disabled={impersonating}
                          className="h-7 px-2 text-[#A0AEC0] hover:text-white hover:bg-[#0A1628]"
                          title="Als User einloggen"
                          data-testid={`impersonate-${u.email}`}
                        >
                          <LogIn className="w-3.5 h-3.5" />
                        </Button>
                        {u.email !== 'ashley@xchannels.ai' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirm({ id: u.id, email: u.email, name: u.name })}
                            className="h-7 px-2 text-[#5A6C7D] hover:text-red-400 hover:bg-[#0A1628]"
                            title="User löschen"
                            data-testid={`delete-user-${u.email}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[#1A2B42] border border-[#0A1628] rounded-lg p-6 max-w-md w-full mx-4 space-y-4" onClick={e => e.stopPropagation()} data-testid="delete-confirm-dialog">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Bist du sicher?</h3>
                <p className="text-[#A0AEC0] text-sm">Diese Aktion kann nicht rückgängig gemacht werden.</p>
              </div>
            </div>
            <div className="bg-[#0A1628] rounded-lg p-3">
              <p className="text-white text-sm font-medium">{deleteConfirm.name || '—'}</p>
              <p className="text-[#5A6C7D] text-xs">{deleteConfirm.email}</p>
              <p className="text-red-400/80 text-xs mt-2">Alle Daten werden gelöscht: Content, Brand DNA, Projekte, Events, Tags, Folders.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                className="text-[#A0AEC0] hover:text-white"
                data-testid="delete-cancel-btn"
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => handleDeleteUser(deleteConfirm.id)}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="delete-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Endgültig löschen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Package Configuration Modal */}
      {customModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="custom-package-modal"
        >
          <div className="bg-[#0f1f36] border border-[#1A2B42] rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-1">Custom Package Config</h3>
            <p className="text-xs text-[#5A6C7D] mb-5">{customModal.email}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[#A0AEC0] mb-1">Credits / Monat</label>
                <input
                  type="number"
                  min="0"
                  value={customModal.credits_monthly}
                  onChange={(e) => setCustomModal({ ...customModal, credits_monthly: e.target.value })}
                  data-testid="custom-credits-input"
                  className="w-full bg-[#0A1628] border border-[#1A2B42] rounded px-3 py-2 text-white text-sm outline-none focus:border-[#00D9C8]"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A0AEC0] mb-1">Max Channels</label>
                <input
                  type="number"
                  min="0"
                  value={customModal.channels}
                  onChange={(e) => setCustomModal({ ...customModal, channels: e.target.value })}
                  data-testid="custom-channels-input"
                  className="w-full bg-[#0A1628] border border-[#1A2B42] rounded px-3 py-2 text-white text-sm outline-none focus:border-[#00D9C8]"
                />
              </div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-white">Video Generation</span>
                <input
                  type="checkbox"
                  checked={customModal.video_generation}
                  onChange={(e) => setCustomModal({ ...customModal, video_generation: e.target.checked })}
                  data-testid="custom-video-toggle"
                  className="w-4 h-4 accent-[#00D9C8] cursor-pointer"
                />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-white">Search Placement</span>
                <input
                  type="checkbox"
                  checked={customModal.search_placement}
                  onChange={(e) => setCustomModal({ ...customModal, search_placement: e.target.checked })}
                  data-testid="custom-search-toggle"
                  className="w-4 h-4 accent-[#00D9C8] cursor-pointer"
                />
              </label>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <Button
                variant="ghost"
                onClick={() => setCustomModal(null)}
                data-testid="custom-cancel-btn"
                className="text-[#A0AEC0]"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCustomModal}
                data-testid="custom-save-btn"
                className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00BFB0]"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
